import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { api, onUnauthenticated, setAccessToken } from '../../lib/apiClient';
import { authApi } from './authApi';
import { AuthContext, type AuthState } from './authContext';
import type { RoleName, User } from '../../types/api';

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [user, setUser] = useState<User | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const bootstrapped = useRef(false);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    // El cliente HTTP avisa cuando el refresco definitivamente ha fallado.
    onUnauthenticated(clearSession);
    return () => {
      onUnauthenticated(null);
    };
  }, [clearSession]);

  useEffect(() => {
    if (bootstrapped.current) {
      return;
    }
    bootstrapped.current = true;

    // Al recargar la pagina el access token se pierde: solo queda la cookie.
    // Un unico intento de refresco decide si habia sesion o no.
    void api
      .refreshSession()
      .then((session) => {
        setUser(session ? session.user : null);
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => {
        setIsBootstrapping(false);
      });
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<User> => {
    const session = await authApi.login(email, password);
    setAccessToken(session.access_token);
    setUser(session.user);
    return session.user;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
    } finally {
      // Aunque el servidor no responda, la sesion local se cierra igual.
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      isBootstrapping,
      isAuthenticated: user !== null,
      hasRole: (...roles: RoleName[]) => (user ? roles.includes(user.role) : false),
      login,
      logout,
    }),
    [user, isBootstrapping, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
